import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import React from "react";
import "../../../../css/Customer/Pages/OurServicesPage/Service.css";
import FAQContainer from "frontend/Components/DesignSystem/FAQ/FAQContainer";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as MortagePlanningIcon } from "../../../../assets/icons/Mortgage-Planning.svg";
import { ReactComponent as CorporatePlanningIcon } from "../../../../assets/icons/Corporate-Planning.svg";
import { ReactComponent as InvestmentPlanningIcon } from "../../../../assets/icons/Investment-Planning.svg";
import { ReactComponent as EquityReleaseIcon } from "../../../../assets/icons/Equity -Release.svg";
import { ReactComponent as InsuranceProtectionIcon } from "../../../../assets/icons/Insurance-Protection.svg";
import AdviserValues from "frontend/Components/AdviserValues/AdviserValue";
import { ReactComponent as NumberOneIcon } from "../../../../assets/icons/Number 1.svg";
import { ReactComponent as NumberTwoIcon } from "../../../../assets/icons/Number 2.svg";
import { ReactComponent as NumberThreeIcon } from "../../../../assets/icons/Number 3.svg";
import { ReactComponent as NumberFourIcon } from "../../../../assets/icons/Number 4.svg";
import Testimonials from "../../../Components/Testimonials/Testimonials";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
// import CustomerRetirementImage from "../../../../../assets/images/customer-retirement-min.jpeg";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";

/**
 * RetirementPlanning component renders the Retirement Planning service page.
 *
 * This page provides information and advice about retirement planning, including FAQs,
 * adviser values, and links to related services. It features a navigation bar, hero section,
 * service cards, testimonials, contact form, and footer.
 *
 * @component
 *
 * @returns {JSX.Element} The rendered Retirement Planning service page.
 *
 * @example
 * // Usage in a React Router route
 * <Route path="/OurServices/RetirementPlanning" element={<RetirementPlanning />} />
 *
 * @remarks
 * - Displays a list of FAQs related to retirement planning.
 * - Shows adviser values with icons.
 * - Provides navigation to other services.
 * - Includes SEO metadata using Helmet.
 */
const RetirementPlanning = () => {
  const navigationItems = [
    { label: "Home", navigateTo: "/" },
    { label: "Our Services", navigateTo: "/OurServices" },
    { label: "About Us", navigateTo: "/AboutUs" },
    { label: "Contact Us", navigateTo: "/ContactUs" },
  ];

  const faqs = [
    {
      question: "When can I retire?",
      answer:
        "The state pension age varies based on your date of birth, but is currently age 66.  It is due to go up to 67 and 68 based on current plans and regulation, though this could change.   Go to the Governments “State Pension Age” calculator to find out what age applies to you .",
    },
    {
      question: "When can I access my personal pension? ",
      answer:
        "At the moment this is 55, but is expected to rise to 57 from 6/4/2028. As a rule of thumb, access to personal pensions is currently designed to be 10 years before your State Pension Age. ",
    },
    {
      question: "Should I combine my pensions?",
      answer:
        "Our advisers can discuss this with you.  There are several different factors to consider, such as whether any valuable guarantees would be lost, and to compare charges.  Generally however, there are benefits to having just one pension to manage, not least to help keep things simple.   ",
    },
    {
      question: "What are my retirement options?",
      answer:
        "There are different types of options including annuity, drawdown, semi-retiring and more.  Our advisers will discuss each of these options with you, based on your situation.   ",
    },
    {
      question: "How much should I pay into my pension?",
      answer:
        "This varies, our advisers can talk with you on your plans, including your retirement goals, affordability, the income due from your state pension, your employer contributions, and more.   ",
    },
    {
      question: "How will my pensions be taxed? ",
      answer:
        "When you pay into a pension, there will normally be generous tax reliefs available, the most common ones being 20% (basic rate) and 40% (higher rate). The basic rate tax relief from the government is added at source.   As an example, if you paid £100 into a pension, £125 would be invested.  This extra boost from the government is really useful, and helps to ensure your pension will grow effectively over many years, on a compounded basis.     ",
    },
    {
      question: "How are pensions regulated?",
      answer:
        "There is a lot of protection in place, principally through the Financial Conduct Authority and The Pensions Regulator.  We will only ever recommend fully regulated pensions, for your protection.  ",
    },
    {
      question: "Who can inherit my pension?",
      answer:
        "With a new Personal Pension, you can complete an “Expression of Wish form” to nominate whoever is more important to you.  Very often this is close family, such as spouses or children.  ",
    },
  ];
  const values = [
    {
      icon: <NumberOneIcon />,
      text: "You are our most important person",
    },
    {
      icon: <NumberTwoIcon />,
      text: "Recommendations are planned out and delivered to you",
    },
    {
      icon: <NumberThreeIcon />,
      text: "Deliver the best outcomes for you",
    },
    {
      icon: <NumberFourIcon />,
      text: "We talk you through every step of the process",
    },
  ];

  return (
    <div className="Service">
      <SeoHelmet
        page={SEO_PAGES.retirementPlanning}
        service={{
          name: "Retirement Planning Advice",
          description:
            "Rosemount Financial Solutions provides retirement planning advice, helping clients review pensions, understand retirement options, plan pension contributions and build a suitable long-term financial strategy.",
          serviceType: [
            "Retirement Planning",
            "Pension Advice",
            "Personal Pension Planning",
            "Retirement Advice",
            "Financial Planning",
            "Pension Contribution Planning",
            "Retirement Income Planning",
            "Long-Term Financial Planning",
          ],
        }}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "Our Services",
            url: getPageUrl("/OurServices"),
          },
          {
            name: "Retirement Planning",
            url: getPageUrl("/OurServices/RetirementPlanning"),
          },
        ]}
        faqs={faqs}
      />
      <NavBar navigationItems={navigationItems} />
      <HeroAnimation
        imgSrc={ImageDirectory["customer-retirement-min"]}
        altText="A couple on a swing set on an autumnal day with brown leaves in the background."
        children={
          <>
            <h1>Retirement Planning</h1>
            <div className="SubTitle">
              Bespoke plans for your long-term future
            </div>
          </>
        }
      />
      <div className="SubTitle-Container">
        <div className="SubTitle">
          Our retirement planning advice helps you build a secure and
          comfortable future by creating a strategy tailored to your goals.
          Whether you are approaching retirement or planning ahead, we offer
          expert advice to provide a retirement designed for your needs.
        </div>
        <div className="SubTitle">
          Our advisers’ will discuss bespoke pension plans for your needs and
          goals. The earlier you start looking at pension plans, the easier it
          is for you long term to know your future is secure and ensure you have
          the retirement you deserve.
        </div>
        <div className="SubTitle">
          The value of pensions and the income they produce can fall as well as
          rise. You may get back less than you invested.
        </div>
        <div className="SubTitle">
          Transferring out of a final salary pension is unlikely to be in the
          best interest of most people.
        </div>
      </div>
      <FAQContainer faqs={faqs} />
      <div className="Customer-Services-Container">
        <h2>Our Services</h2>
        <div className="Customer-Services">
          <CardProduct
            Icon={MortagePlanningIcon}
            title="Mortgage Planning"
            link="/OurServices/MortgagePlanning"
          />
          <CardProduct
            Icon={CorporatePlanningIcon}
            title="Corporate Planning"
            link="/OurServices/CorporatePlanning"
          />
          <CardProduct
            Icon={InvestmentPlanningIcon}
            title="Investment Planning"
            link="/OurServices/InvestmentPlanning"
          />
          <CardProduct
            Icon={EquityReleaseIcon}
            title="Equity Release"
            link="/OurServices/EquityRelease"
          />
          <CardProduct
            Icon={InsuranceProtectionIcon}
            title="Insurance/ Protection"
            link="/OurServices/InsuranceProtection"
          />
        </div>
      </div>
      <AdviserValues title="Our Adviser Values" values={values} />
      <Testimonials />
      <ContactUs />
      <Footer />
    </div>
  );
};

export default RetirementPlanning;
